import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getApiUrl } from '../config/api';
import type { Brand, Category } from '../types';

interface LayoutProps {
  children: ReactNode;
}

const VERTICAL_SECTIONS: { key: Category; label: string; icon: string }[] = [
  { key: 'print',           label: 'Print',           icon: '🖨️' },
];

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isPartner = user?.role === 'partner';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({
    print: true,
  });

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isPartner) return;
    fetch(getApiUrl('/api/brands'), { credentials: 'include' })
      .then(r => r.ok ? r.json() : { brands: [] })
      .then(d => setBrands(d.brands || []))
      .catch(() => {});
  }, [isPartner]);

  const topNav = isPartner
    ? [
        { name: 'Leads',      href: '/leads',   icon: '📋' },
        { name: 'My Account', href: '/account', icon: '👤' },
      ]
    : [
        { name: 'Dashboard',        href: '/',                 icon: '📊' },
        { name: 'Leads',            href: '/leads',            icon: '📋' },
        { name: 'Contact Messages', href: '/contact-messages', icon: '✉️' },
      ];

  const contentNav = [
    { name: 'Products',        href: '/products',       icon: '📦' },
    { name: 'Blog & Articles', href: '/blog',           icon: '📝' },
    { name: 'Knowledge Base',  href: '/knowledge-base', icon: '📚' },
  ];

  const systemNav = [
    { name: 'Printer Settings', href: '/printer-settings', icon: '🖨️' },
    { name: 'Users',            href: '/users',            icon: '👥' },
    { name: 'Companies',        href: '/companies',        icon: '🤝' },
    { name: 'Settings',         href: '/settings',         icon: '⚙️' },
  ];

  const toggleSection = (key: string) => {
    setSectionOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (href: string) => location.pathname === href;

  const navLinkClass = (active: boolean) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
      active ? 'bg-blue-600 text-white' : 'text-dark-text hover:bg-slate-700'
    }`;

  return (
    <div className="flex h-screen bg-dark-bg">
      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between bg-dark-surface border-b border-dark-border px-4 py-3 md:hidden">
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-dark-text p-1"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-white">Britannia LeadGen</h1>
        <div className="w-6" />
      </div>

      {/* Backdrop overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-dark-surface border-r border-dark-border flex flex-col
          transform transition-transform duration-200 ease-in-out
          md:relative md:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="p-6 border-b border-dark-border flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Britannia LeadGen</h1>
            <p className="text-sm text-dark-text-secondary">{isPartner ? 'Partner Portal' : 'Admin Dashboard'}</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-dark-text-secondary hover:text-white md:hidden"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Top nav items */}
          {topNav.map(item => (
            <Link key={item.href} to={item.href} className={navLinkClass(isActive(item.href))}>
              <span className="text-xl">{item.icon}</span>
              <span className="font-medium">{item.name}</span>
            </Link>
          ))}

          {/* Vertical sections */}
          {!isPartner && VERTICAL_SECTIONS.map(section => {
            // Fallback: derive category from vertical for brands that predate migration 012
            const verticalToCategory: Record<string, string> = { copiers: 'print' };
            const sectionBrands = brands.filter(b => (b.category || verticalToCategory[b.vertical] || 'print') === section.key);
            const isComingSoon = false;
            const open = sectionOpen[section.key];

            return (
              <div key={section.key} className="mt-2">
                <div className="border-t border-dark-border pt-2">
                  <button
                    onClick={() => toggleSection(section.key)}
                    disabled={isComingSoon}
                    className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-left transition-colors ${
                      isComingSoon
                        ? 'text-dark-text-secondary cursor-not-allowed opacity-50'
                        : 'text-dark-text-secondary hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                      <span>{section.icon}</span>
                      <span>{section.label}</span>
                      {isComingSoon && <span className="text-xs font-normal normal-case text-dark-text-secondary ml-1">(coming soon)</span>}
                    </span>
                    {!isComingSoon && (
                      <span className="text-xs">{open ? '▾' : '▸'}</span>
                    )}
                  </button>

                  {!isComingSoon && open && (
                    <div className="ml-2 mt-1 space-y-0.5">
                      {/* Brand links */}
                      {sectionBrands.map(brand => {
                        const brandPath = `/brands/${brand.id}`;
                        return (
                          <Link
                            key={brand.id}
                            to={brandPath}
                            className={navLinkClass(isActive(brandPath)) + ' py-2 text-sm'}
                          >
                            <span className="text-base">•</span>
                            <span>{brand.name}</span>
                          </Link>
                        );
                      })}

                      {sectionBrands.length === 0 && (
                        <p className="px-4 py-2 text-xs text-dark-text-secondary italic">No brands</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Content section */}
          {!isPartner && (
            <div className="border-t border-dark-border pt-2 mt-2 space-y-1">
              <p className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-dark-text-secondary">
                Content
              </p>
              {contentNav.map(item => (
                <Link key={item.href} to={item.href} className={navLinkClass(isActive(item.href))}>
                  <span className="text-xl">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}
            </div>
          )}

          {/* System section */}
          {!isPartner && (
            <div className="border-t border-dark-border pt-2 mt-2 space-y-1">
              <p className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-dark-text-secondary">
                System
              </p>
              {systemNav.map(item => (
                <Link key={item.href} to={item.href} className={navLinkClass(isActive(item.href))}>
                  <span className="text-xl">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-dark-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
              {user?.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-dark-text truncate">{user?.name}</p>
              <p className="text-xs text-dark-text-secondary truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full btn btn-secondary text-sm"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 md:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
